import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Yup from 'yup';
import { Formik, useFormikContext, getIn } from 'formik';
import { Button, Grid } from '@material-ui/core';
import {
  FarmRiskType,
  FarmScheduleCoverageTypesByState,
  lookupsJson,
  ScheduledFarmItemTypes,
  FarmScheduleCoverageType
} from '@ourbranch/lookups';

import { brandConfig } from 'brand-config';
import { BrandFormField } from 'common/components/farm/brand-form-field';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import InfoIcon from 'core/assets/svg/ic-info-dark.svg?react';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { Animals4HFFAForm, FarmIncomeCoverageForm, PeakSeasonInventoryForm } from '../../sub-risks';
import ScheduledFarmPropertyItem from './scheduled-farm-property-item';
import useStyles from './FO6-scheduled.styles';

const {
  scheduledFarmPropertyItemTypesByState,
  deductibleAllOther,
  earthquakeDeductible,
  scheduledFarmPropertyCategories
} = lookupsJson.farm;
const eqDeductibleOptions = earthquakeDeductible.filter((d) => d.id !== 0.02);

const riskType = FarmRiskType.Fo6Scheduled;

const initialValues = {
  category: undefined,
  description: undefined,
  deductible: undefined,
  limit: undefined,
  serialNumber: undefined,
  make: undefined,
  hasReplacementCostCoverage: false,
  hasOpenPerils: false,
  increaseStackLimit: undefined,
  livestockCategory: undefined,
  includeWinterPeril: undefined,
  hasWinterPerils: false,
  hasSuffocationOfLivestock: false,
  hasSpecialCattleEndorsement: false
};

const validationSchema = () =>
  Yup.object().shape({
    category: Yup.string().required('Category is required'),
    description: Yup.string().required('Description is required'),
    deductible: Yup.number().required('Deductible is required'),
    limit: Yup.number().required('Coverage limit is required').positive('Coverage limit must be greater than 0'),
    serialNumber: Yup.string().when('category', {
      is: (val) => val === ScheduledFarmItemTypes.COMBINES || val === ScheduledFarmItemTypes.CUSTOM_FARM_MACHINERY,
      then: (schema) => schema.required('Serial number is required')
    }),
    make: Yup.string().when('category', {
      is: (val) => val === ScheduledFarmItemTypes.COMBINES || val === ScheduledFarmItemTypes.CUSTOM_FARM_MACHINERY,
      then: (schema) => schema.required('Make is required')
    }),
    livestockCategory: Yup.string().when('category', {
      is: ScheduledFarmItemTypes.LIVESTOCK,
      then: (schema) => schema.required('Livestock category is required')
    }),
    additionalPerils: Yup.number().when('category', {
      is: ScheduledFarmItemTypes.LIVESTOCK,
      then: (schema) => schema.required('Additional perils option is required')
    })
  });

const ScheduledFarmProperty = observer(({ disabled, fieldName, riskIndex }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();

  const locationRisks = getIn(values, `${fieldName}.risks`);

  const coverageFieldName = `${fieldName}.risks[${riskIndex}].coverages.coverageF`;
  const scheduledItems = getIn(values, coverageFieldName) || [];

  const address = inPolicyRoute ? getIn(values, `${fieldName}.address`) : getIn(values, 'correctedAddress');

  const riskFieldName = `${fieldName}.risks[${riskIndex}]`;

  const { disableForm, setActiveForm } = useActiveFormState(riskFieldName);
  const disableFormFinal = Boolean(disableForm || disabled);

  const availableCoverageFCategories =
    brandConfig.farm[address.state]?.[riskType]?.coverageF?.itemTypes || scheduledFarmPropertyCategories;
  const availableScheduledCoverageTypes =
    brandConfig.farm[address.state]?.[riskType]?.availableScheduledCoverageTypes ||
    FarmScheduleCoverageTypesByState[address.state] ||
    [];

  const handleRemoveRisk = () => {
    const newRisks = locationRisks.filter((_, index) => index !== riskIndex);
    setFieldValue(`${fieldName}.risks`, newRisks);
    setFieldTouched(`${fieldName}.risks`);
    setActiveForm(null);
  };

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">FO-6 Scheduled farm property</Label>
          </div>
        </div>
      }
      contentRight={
        <Field id={fieldName} name={fieldName} type="remove" disabled={disableFormFinal} onClick={handleRemoveRisk} />
      }
      initialOpen={true}
    >
      <Grid className={classes.coverageCard}>
        <FormSectionWrapper
          sectionTitle="Scheduled farm property (F)"
          totalLabel="items"
          totalCount={scheduledItems?.length || 0}
          emptyStateTitle="There are no items added yet"
          mode="light"
          containerClassName={classes.noBorderContainer}
        >
          {/* list items */}
          {scheduledItems.map((item, index) => (
            <ScheduledFarmPropertyItem
              key={`farm-property-${index}`}
              item={item}
              index={index}
              fieldName={fieldName}
              coverageType="coverageF"
              onDelete={(itemIndex) => {
                const newItems = scheduledItems.filter((_, checkIndex) => checkIndex !== itemIndex);
                setFieldValue(coverageFieldName, newItems);
                setFieldTouched(coverageFieldName);
              }}
              disabled={disableFormFinal}
              state={address.state}
            />
          ))}

          {/* totals row */}
          {scheduledItems.length > 0 && (
            <div className={classes.totalsItem}>
              <div className={classes.totalsItemTitleRow}>
                <Grid xs={12}>
                  <div className={classes.totalsItemName}>
                    <InfoIcon width={16} height={16} />
                    Totals
                  </div>
                </Grid>
                <Grid className={classes.totalsItemStack} xs={3}>
                  <span className={classes.totalsItemLabel}>Deductible</span>
                  <span className={classes.totalsItemValue}>
                    ${scheduledItems.reduce((total, item) => total + (item.deductible || 0), 0).toLocaleString()}
                  </span>
                </Grid>
                <div className={classes.totalsDivider} />
                <Grid className={classes.totalsItemStack} xs={3}>
                  <span className={classes.totalsItemLabel}>Coverage</span>
                  <span className={classes.totalsItemValue}>
                    ${scheduledItems.reduce((total, item) => total + (item.limit || 0), 0).toLocaleString()}
                  </span>
                </Grid>
              </div>
            </div>
          )}

          {/* add form */}
          <div className={classes.addFormContainer}>
            <Formik
              initialValues={initialValues}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              onSubmit={(newItem, { resetForm }) => {
                console.log({ newItem, coverageFieldName });
                Object.keys(newItem).forEach((key) => {
                  if (newItem[key] === '') {
                    newItem[key] = null;
                  }
                });

                const currentItems = scheduledItems || [];
                setFieldValue(coverageFieldName, [...currentItems, newItem]);
                setFieldTouched(coverageFieldName);
                resetForm();
              }}
            >
              {({ submitForm, values, setFieldValue }) => {
                useEffect(() => {
                  // Default deductible and limit values when category is selected to speed up the adding process
                  if (values.category) {
                    if (!values.deductible) {
                      setFieldValue('deductible', deductibleAllOther[0].id);
                    }
                    if (!values.limit) {
                      setFieldValue('limit', 0);
                    }
                  }
                }, [values.category]);

                return (
                  <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    <Field
                      xs={6}
                      type="select"
                      id="category"
                      name="category"
                      mode="light"
                      label="Type of item"
                      options={availableCoverageFCategories}
                      disabled={disableFormFinal}
                    />
                    <Field
                      xs={6}
                      type="string"
                      id="scheduledItemName"
                      name="description"
                      mode="light"
                      label="Item name"
                      disabled={disableFormFinal}
                    />
                    <Field
                      xs={6}
                      type="select"
                      id="deductible"
                      name="deductible"
                      mode="light"
                      label="Deductible"
                      options={deductibleAllOther}
                      disabled={disableFormFinal}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      id="limit"
                      name="limit"
                      mode="light"
                      label="Coverage limit"
                      format={{ thousandSeparator: true, prefix: '$' }}
                      disabled={disableFormFinal}
                    />

                    {[
                      ScheduledFarmItemTypes.COMBINES,
                      ScheduledFarmItemTypes.CUSTOM_FARM_MACHINERY,
                      ScheduledFarmItemTypes.DESCRIBED_MACHINERY,
                      ScheduledFarmItemTypes.IRRIGATION_EQUIPMENT,
                      ScheduledFarmItemTypes.MACHINERY_NOT_DESCRIBED,
                      ScheduledFarmItemTypes.RENTED_OR_BORROWED_EQUIPMENT
                    ].includes(values.category) && (
                      <>
                        <Field
                          xs={6}
                          type="string"
                          id="serialNumber"
                          name="serialNumber"
                          mode="light"
                          label="Serial number"
                          disabled={disableFormFinal}
                        />
                        <Field
                          xs={6}
                          type="string"
                          id="make"
                          name="make"
                          mode="light"
                          label="Make/model"
                          disabled={disableFormFinal}
                        />
                      </>
                    )}

                    {[
                      ScheduledFarmItemTypes.COMBINES,
                      ScheduledFarmItemTypes.DESCRIBED_MACHINERY,
                      ScheduledFarmItemTypes.IRRIGATION_EQUIPMENT
                    ].includes(values.category) && (
                      <>
                        <Field
                          xs={6}
                          type="checkbox"
                          id="hasReplacementCostCoverage"
                          name="hasReplacementCostCoverage"
                          mode="light"
                          label="Replacement cost coverage"
                          disabled={disableFormFinal}
                        />
                        <Field
                          xs={6}
                          type="checkbox"
                          id="hasOpenPerils"
                          name="hasOpenPerils"
                          mode="light"
                          label="Open perils"
                          subLabel="Including intake of foreign objects"
                          disabled={disableFormFinal}
                        />
                      </>
                    )}

                    {values.category === ScheduledFarmItemTypes.LIVESTOCK && (
                      <>
                        <Field
                          xs={6}
                          type="select"
                          id="livestockCategory"
                          name="livestockCategory"
                          mode="light"
                          label="Livestock type"
                          options={lookupsJson.farm.livestockTypes}
                          disabled={disableFormFinal}
                          onChange={(val) => {
                            if (val !== 'cattle') {
                              setFieldValue('hasSpecialCattleEndorsement', false);
                            }
                          }}
                        />
                        <Field
                          xs={6}
                          type="select"
                          id="additionalPerils"
                          name="additionalPerils"
                          mode="light"
                          label="Additional Perils"
                          options={lookupsJson.farm.livestockAdditionalPerils}
                          disabled={disableFormFinal}
                        />
                        <Field
                          xs={6}
                          type="checkbox"
                          id="hasWinterPerils"
                          name="hasWinterPerils"
                          mode="light"
                          label="Winter perils"
                          disabled={disableFormFinal}
                        />
                        <Field
                          xs={6}
                          type="checkbox"
                          id="hasSuffocationOfLivestock"
                          name="hasSuffocationOfLivestock"
                          mode="light"
                          label="Suffocation of livestock"
                          disabled={disableFormFinal}
                        />
                        <Field
                          xs={6}
                          type="checkbox"
                          id="hasSpecialCattleEndorsement"
                          name="hasSpecialCattleEndorsement"
                          mode="light"
                          label="Special cattle endorsement"
                          subLabel="Multiple loss coverage"
                          disabled={disableForm || values.livestockCategory !== 'cattle'}
                        />
                      </>
                    )}

                    <Grid
                      container
                      justifyContent={
                        values.category === ScheduledFarmItemTypes.LIVESTOCK ? 'space-between' : 'flex-end'
                      }
                      className={classes.sectionFooter}
                    >
                      {values.category === ScheduledFarmItemTypes.LIVESTOCK && (
                        <Grid className={classes.livestockInfoTextContainer} xs={9}>
                          <InfoIcon width={16} height={16} />
                          <span className={classes.livestockInfoText}>
                            This is for livestock only. If you’d like to add animals (Under 4-H & FFA programs), please
                            go to that section.
                          </span>
                        </Grid>
                      )}
                      <Button
                        type="button"
                        mode="big"
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        disabled={disableFormFinal}
                        onClick={submitForm}
                      >
                        Add Item
                      </Button>
                    </Grid>
                  </Grid>
                );
              }}
            </Formik>
          </div>
        </FormSectionWrapper>
      </Grid>
      <Grid container spacing={2} className={classes.greenCoverageCard}>
        <Grid item xs={12}>
          <Label type="formSubTitle">Coverages</Label>
        </Grid>

        <BrandFormField
          xs={12}
          id={`${fieldName}.risks[${riskIndex}].coverages.deductibleEarthquake`}
          name={`${fieldName}.risks[${riskIndex}].coverages.deductibleEarthquake`}
          type="select"
          options={eqDeductibleOptions}
          mode="dark"
          label="Earthquake deductible"
          disabled={disableForm}
          riskType={riskType}
          state={address.state}
        />

        <Field
          xs={6}
          name={`${fieldName}.risks[${riskIndex}].coverages.expandedDebrisRemoval`}
          id={`${fieldName}.risks[${riskIndex}].coverages.expandedDebrisRemoval`}
          type="checkbox"
          label="Expanded debris removal"
          mode="dark"
          disabled={disableForm}
        />

        <Field
          xs={6}
          name={`${fieldName}.risks[${riskIndex}].coverages.hasSnowWeightCoverage`}
          id={`${fieldName}.risks[${riskIndex}].coverages.hasSnowWeightCoverage`}
          type="checkbox"
          label="Weight of snow and ice coverage"
          mode="dark"
          disabled={disableForm}
        />

        {/* Animals 4-H & FFA */}
        {availableScheduledCoverageTypes.includes(FarmScheduleCoverageType.ANIMALS_4H_FFA) && (
          <Grid item xs={12}>
            <Animals4HFFAForm
              fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
              disabled={disableForm}
            />
          </Grid>
        )}

        {/* Farm Income Coverage */}
        {availableScheduledCoverageTypes.includes(FarmScheduleCoverageType.INCOME) && (
          <Grid item xs={12}>
            <FarmIncomeCoverageForm
              fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
              disabled={disableForm}
            />
          </Grid>
        )}

        {/* Peak Season Inventory */}
        {availableScheduledCoverageTypes.includes(FarmScheduleCoverageType.PEAK) && (
          <Grid item xs={12}>
            <PeakSeasonInventoryForm
              fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
              disabled={disableForm}
              state={address.state}
            />
          </Grid>
        )}
      </Grid>
    </CollapsibleCard>
  );
});

ScheduledFarmProperty.propTypes = {
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired,
  riskIndex: PropTypes.number.isRequired
};

ScheduledFarmProperty.defaultProps = {
  disabled: false
};

export default ScheduledFarmProperty;
